<template>
  <div v-if="lesson" class="cook" @pointerdown.once="primeAudio">
    <!-- The prep, one screen each, every one saying HOW (Matt, 2026-10-02:
         the old six-item checklist assumed he already knew how to mince a
         shallot; the course is meant to teach). -->
    <section v-if="phase === 'prep'" class="page stack cook__step">
      <p class="eyebrow">Prep {{ prepIndex + 1 }} of {{ lesson.mise.length }} · then cooking</p>
      <p v-if="prepIndex === 0" class="muted">Everything gets ready before the cooking starts, one thing at a time. Each screen says how.</p>
      <DoNow :text="prepStep.text" :needs="prepStep.needs"/>
      <!-- The how, a sentence a line (Matt, 2026-10-02: "just like a block
           of text ... more than I can sort of put in my head"), smaller
           than the banner and labelled as this step's, not what comes next
           (household feedback, 2026-10-12). -->
      <div v-if="prepStep.how" class="cook__how">
        <p class="cook__label">How, move by move</p>
        <ol class="cook__lines">
          <li v-for="(line, i) in sentences(prepStep.how)" :key="i"><Amounts :text="line"/></li>
        </ol>
      </div>
      <p v-if="prepStep.tip && showTips" class="cook__note cook__tip"><span class="cook__label">Why</span><Amounts :text="prepStep.tip"/></p>
      <p v-if="prepStep.check" class="cook__note cook__check"><span class="cook__label">Done looks like</span><Amounts :text="prepStep.check"/></p>

      <NextUp :next="upNext"/>

      <div class="cook__nav">
        <button type="button" class="btn btn-lg" :disabled="prepIndex === 0" @click="goPrep(-1)">Back</button>
        <button v-if="prepIndex < lesson.mise.length - 1" type="button" class="btn btn-primary btn-lg" @click="goPrep(1)">Next</button>
        <button v-else type="button" class="btn btn-success btn-lg" @click="startSteps">Start cooking</button>
      </div>
      <ol class="cook__dots" aria-hidden="true">
        <li v-for="(s, i) in lesson.mise" :key="i" :class="{ on: i === prepIndex, past: i < prepIndex }" @click="prepIndex = i"></li>
      </ol>
      <div class="cook__exits">
        <button type="button" class="cook__quit" @click="quit">Leave cook mode</button>
        <button type="button" class="cook__quit" :class="{ 'cook__quit--sure': restartArmed }" @click="startOver">{{ restartArmed ? 'Tap again to start over' : 'Start over' }}</button>
      </div>
    </section>

    <!-- The steps, one screen each. -->
    <section v-else-if="phase === 'steps'" class="page stack cook__step">
      <p class="eyebrow">Cooking · step {{ stepIndex + 1 }} of {{ lesson.steps.length }}</p>
      <DoNow :text="step.text" :needs="step.needs"/>
      <!-- The hands-on version, to tick off as you go (Matt, 2026-10-02:
           "I wish these instructions were more hands-on"). Smaller than the
           banner and labelled as how to do THIS step: unlabelled, they read
           as a preview of what comes next (household feedback, 2026-10-12). -->
      <div v-if="step.do?.length" class="stack cook__do">
        <p class="cook__label">How, move by move</p>
        <label v-for="(move, i) in step.do" :key="`${stepIndex}-${i}`" class="check" :class="{ 'check--done': isTicked(stepIndex, i) }">
          <input type="checkbox" class="check__box" :checked="isTicked(stepIndex, i)" @change="tick(stepIndex, i, $event.target.checked)">
          <span class="check__text"><Amounts :text="move"/></span>
        </label>
      </div>
      <p v-if="step.tip && showTips" class="cook__note cook__tip"><span class="cook__label">Why</span><Amounts :text="step.tip"/></p>
      <p v-if="step.check" class="cook__note cook__check"><span class="cook__label">Done looks like</span><Amounts :text="step.check"/></p>

      <div v-if="step.timer" class="row">
        <button v-if="!timerForStep(stepIndex)" type="button" class="btn btn-primary btn-lg" @click="startTimer(step.timer, stepIndex)">
          Start {{ formatSeconds(step.timer.seconds) }} timer · {{ step.timer.label }}
        </button>
        <span v-else class="pill pill--green">Timer running · {{ step.timer.label }}</span>
      </div>

      <NextUp :next="upNext"/>

      <div class="cook__nav">
        <button type="button" class="btn btn-lg" @click="go(-1)">Back</button>
        <button v-if="stepIndex < lesson.steps.length - 1" type="button" class="btn btn-primary btn-lg" @click="go(1)">Next</button>
        <button v-else type="button" class="btn btn-success btn-lg" @click="finish">It’s on the plate</button>
      </div>
      <ol class="cook__dots" aria-hidden="true">
        <li v-for="(s, i) in lesson.steps" :key="i" :class="{ on: i === stepIndex, past: i < stepIndex }" @click="stepIndex = i"></li>
      </ol>
      <div class="cook__exits">
        <button type="button" class="cook__quit" @click="quit">Leave cook mode</button>
        <button type="button" class="cook__quit" :class="{ 'cook__quit--sure': restartArmed }" @click="startOver">{{ restartArmed ? 'Tap again to start over' : 'Start over' }}</button>
      </div>
    </section>

    <!-- Done. -->
    <section v-else class="page stack">
      <p class="eyebrow">Lesson {{ lesson.number }}</p>
      <h1>On the plate.</h1>
      <p class="muted">{{ lesson.plate }}</p>
      <p>Eat. Then, when everyone’s done, one button and everybody says what they thought.</p>
      <router-link :to="`/debrief/${lesson.id}`" class="btn btn-primary btn-lg btn-block">We ate. Talk.</router-link>
      <router-link to="/" class="btn btn-block">Later</router-link>
    </section>

    <TimerBar :timers="visibleTimers" :probe="probeShown" :probe-status="probeChipStatus" @extend="extendTimer" @dismiss="dismissTimer"/>
  </div>
</template>

<script>
// Cook mode. Big type, one step per screen, timers the app owns, the
// screen kept awake, and Claude a tap away. State survives a reload
// (sessionStorage): a phone locked mid-step comes back where it was, and
// timers are wall-clock so they come back right too.
//
// And it is SHARED: the same state is written to the lesson's `live` node
// and followed on every device (Matt, 2026-10-02: "switch back and forth
// between my iPad and my phone and it'll resume where I was"). Newest write
// wins; see utils/liveCook.js. Finishing clears the node.
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { trackEvent } from '../utils/usage.js';
import { useRouter, useRoute } from 'vue-router';
import TimerBar from './TimerBar.vue';
import NextUp from './NextUp.vue';
import Amounts from './Amounts.vue';
import DoNow from './DoNow.vue';
import { useRecipe } from '../utils/recipes.js';
import { logTrace, tracePath } from '../utils/trace.js';
import { createTimer, extend, isDone, formatSeconds, chime, primeAudio, notifyDone } from '../utils/timers.js';
import { markLesson, coursePath } from '../utils/course.js';
import { dbPut, dbDelete, dbGet } from '../store/db.js';
import { subscribe } from '../utils/live.js';
import { deviceId, liveSnapshot, isNewer } from '../utils/liveCook.js';
import { sentences, headline } from '../utils/readable.js';
import { scrollPageToTop } from '../utils/pageScroll.js';
import { store, setActivity, setAppError, setWhere } from '../state.js';
import { useProbe, probeChip } from '../utils/probe.js';
import { announce } from '../utils/teacher.js';

const SESSION_KEY = (id) => `enjoyCookingCook:${id}`;

export default {
  name: 'CookScreen',
  components: { TimerBar, NextUp, Amounts, DoNow },
  props: { id: { type: String, required: true }, hat: { type: String, default: null } },
  setup (props) {
    const router = useRouter();
    // The household's own copy (utils/recipes.js): the teacher may have revised it, even mid-cook.
    const lesson = useRecipe(computed(() => props.hat), computed(() => props.id));
    const showTips = computed(() => store.state.preferences?.showTips !== false);
    const phase = ref('prep');
    const prepIndex = ref(0);
    const stepIndex = ref(0);
    const timers = ref([]);
    const ticks = ref({});
    const rung = new Set();
    let wakeLock = null;
    let watchTick = null;
    // --- shared between devices ---------------------------------------------
    const me = deviceId();
    const livePath = () => `${coursePath(props.hat)}/lessons/${props.id}/live`;
    let liveUpdatedAt = 0;   // the newest snapshot this device wrote or applied
    let applying = false;    // an incoming snapshot is being applied: don't echo it
    let pushTimer = null;
    let unsubscribe = null;
    function pushLive () {
      if (!props.hat || applying) return;
      clearTimeout(pushTimer);
      pushTimer = setTimeout(() => {
        const snap = liveSnapshot({ phase: phase.value, prepIndex: prepIndex.value, stepIndex: stepIndex.value, timers: timers.value, ticks: ticks.value }, me);
        liveUpdatedAt = snap.updatedAt;
        dbPut(livePath(), snap).catch(() => { /* offline: the next change tries again */ });
      }, 300);
    }
    function applyLive (data) {
      if (!isNewer(data, liveUpdatedAt)) return;
      applying = true;
      liveUpdatedAt = data.updatedAt;
      phase.value = data.phase || 'prep';
      prepIndex.value = data.prepIndex || 0;
      stepIndex.value = data.stepIndex || 0;
      timers.value = data.timers || [];
      ticks.value = data.ticks || {};
      setTimeout(() => { applying = false; }, 0);
    }
    setActivity('cooking');
    // Where we are, for the teacher — and the trace of what happened, for
    // the teacher's "how tonight is going" (utils/trace.js).
    watch([phase, prepIndex, stepIndex], ([ph, pi, si], old) => {
      setWhere({ screen: 'cook', lessonId: props.id, phase: ph, stepIndex: ph === 'prep' ? pi : si });
      if (old && !applying) {
        const [oph, opi, osi] = old;
        if (ph !== oph) logTrace(props.hat, props.id, { type: ph === 'steps' ? 'cooking' : ph === 'done' ? 'done' : 'phase', phase: ph });
        else if (ph === 'prep' && pi !== opi) logTrace(props.hat, props.id, { type: 'step', phase: 'prep', index: pi, delta: pi - opi });
        else if (ph === 'steps' && si !== osi) logTrace(props.hat, props.id, { type: 'step', phase: 'steps', index: si, delta: si - osi });
      }
    }, { immediate: true });
    // The teacher's go_to_step (state.js sendCommand).
    watch(() => store.state.command, (cmd) => {
      if (!cmd || cmd.type !== 'go_to_step' || !lesson.value) return;
      const n = Math.max(1, Number(cmd.step) || 1) - 1;
      if (cmd.phase === 'prep') { phase.value = 'prep'; prepIndex.value = Math.min(lesson.value.mise.length - 1, n); } else { phase.value = 'steps'; stepIndex.value = Math.min(lesson.value.steps.length - 1, n); }
      scrollPageToTop();
    });

    // --- persistence -------------------------------------------------------
    function restore () {
      applying = true; // a restore is not a move: no trace, no echo
      setTimeout(() => { applying = false; }, 0);
      try {
        const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY(props.id)) || 'null');
        if (saved) {
          // 'mise' is the old checklist phase; a session saved by it
          // starts the prep from the top.
          phase.value = saved.phase === 'mise' ? 'prep' : (saved.phase || 'prep');
          prepIndex.value = Math.min(saved.prepIndex || 0, Math.max(0, (lesson.value?.mise.length || 1) - 1));
          stepIndex.value = saved.stepIndex || 0;
          timers.value = saved.timers || [];
          ticks.value = saved.ticks || {};
          liveUpdatedAt = saved.updatedAt || 0;
        }
      } catch { /* start fresh */ }
    }
    function persist () {
      try {
        sessionStorage.setItem(SESSION_KEY(props.id), JSON.stringify({ phase: phase.value, prepIndex: prepIndex.value, stepIndex: stepIndex.value, timers: timers.value, ticks: ticks.value, updatedAt: liveUpdatedAt }));
      } catch { /* nothing */ }
    }
    watch([phase, prepIndex, stepIndex, timers, ticks], () => { pushLive(); persist(); }, { deep: true });

    // --- prep ----------------------------------------------------------------
    const prepStep = computed(() => lesson.value?.mise[prepIndex.value] || {});
    const goPrep = (delta) => { prepIndex.value = Math.min(lesson.value.mise.length - 1, Math.max(0, prepIndex.value + delta)); scrollPageToTop(); };
    function startSteps () {
      trackEvent('cook-started');
      phase.value = 'steps';
      stepIndex.value = 0;
      scrollPageToTop();
      if (props.hat) markLesson(props.hat, lesson.value.id, { startedAt: Date.now() }).catch(() => {});
    }

    // --- steps ---------------------------------------------------------------
    const step = computed(() => lesson.value?.steps[stepIndex.value] || {});
    const go = (delta) => {
      // Back from the first step is back into the prep, not nowhere.
      if (stepIndex.value + delta < 0) { phase.value = 'prep'; prepIndex.value = lesson.value.mise.length - 1; scrollPageToTop(); return; }
      stepIndex.value = Math.min(lesson.value.steps.length - 1, Math.max(0, stepIndex.value + delta));
      scrollPageToTop();
    };

    // The hands-on moves ticked so far, by step: { [stepIndex]: [i, ...] }.
    const isTicked = (s, i) => (ticks.value[s] || []).includes(i);
    function tick (s, i, on) {
      logTrace(props.hat, props.id, { type: 'tick', stepIndex: s, move: i, on });
      const rest = (ticks.value[s] || []).filter((n) => n !== i);
      ticks.value = { ...ticks.value, [s]: on ? [...rest, i] : rest };
    }

    // --- what comes next ----------------------------------------------------
    // Matt, 2026-10-02: "it would also be really nice to be able to see what
    // the next thing I'm gonna do is". The last prep screen previews the
    // first cooking step; the last cooking step previews the plating.
    const upNext = computed(() => {
      const l = lesson.value;
      if (!l) return null;
      const asNext = (s, label) => (s ? { label, text: headline(s.text).first, timer: s.timer || null } : null);
      if (phase.value === 'prep') {
        return prepIndex.value < l.mise.length - 1
          ? asNext(l.mise[prepIndex.value + 1], 'After this')
          : asNext(l.steps[0], 'Then the cooking starts');
      }
      if (phase.value === 'steps') {
        return stepIndex.value < l.steps.length - 1
          ? asNext(l.steps[stepIndex.value + 1], 'After this')
          : (l.plate ? { label: 'Then onto the plate', text: l.plate, timer: null } : null);
      }
      return null;
    });

    // --- timers --------------------------------------------------------------
    const visibleTimers = computed(() => timers.value.filter((t) => !t.dismissed));
    const timerForStep = (i) => visibleTimers.value.find((t) => t.stepIndex === i);
    function startTimer (spec, i) {
      primeAudio();
      logTrace(props.hat, props.id, { type: 'timer-started', label: spec.label, seconds: spec.seconds, stepIndex: i });
      timers.value = [...timers.value, createTimer({ label: spec.label, seconds: spec.seconds, stepIndex: i })];
    }
    function extendTimer (id, seconds) {
      logTrace(props.hat, props.id, { type: 'timer-extended', label: timers.value.find((t) => t.id === id)?.label, seconds });
      timers.value = timers.value.map((t) => (t.id === id ? extend(t, seconds) : t));
      rung.delete(id);
    }
    function dismissTimer (id) {
      timers.value = timers.value.map((t) => (t.id === id ? { ...t, dismissed: true } : t));
    }
    function watchTimers () {
      for (const t of visibleTimers.value) {
        if (isDone(t) && !rung.has(t.id)) {
          rung.add(t.id);
          chime();
          notifyDone(t);
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        }
      }
    }

    // --- the screen stays on -------------------------------------------------
    async function keepAwake () {
      try {
        if ('wakeLock' in navigator && !wakeLock) wakeLock = await navigator.wakeLock.request('screen');
      } catch { /* not allowed; nothing to do */ }
    }
    function onVisible () { if (document.visibilityState === 'visible') keepAwake(); }

    // --- finish / quit -------------------------------------------------------
    async function finish () {
      trackEvent('cook-finished');
      phase.value = 'done';
      timers.value = [];
      ticks.value = {};
      try {
        if (props.hat) {
          await markLesson(props.hat, lesson.value.id, { cookedAt: Date.now() });
          clearTimeout(pushTimer);
          await dbDelete(livePath()).catch(() => {});
        }
      } catch (error) {
        setAppError(`Cooked, but could not save it: ${error.message}`);
      }
    }
    // START OVER (Matt, 2026-10-15: "When I opened the cooking app it dropped
    // me right in the place where I was when I gave up. We need a reset
    // button."). Back to the first prep screen with no timers or ticks, on
    // every device (the live node goes), and the attempt's trace is filed
    // under `earlier/` so the teacher's "how tonight is going" starts fresh.
    // Two taps, so a stray one costs nothing.
    const restartArmed = ref(false);
    let restartTimer = null;
    async function startOver () {
      if (!restartArmed.value) {
        restartArmed.value = true;
        clearTimeout(restartTimer);
        restartTimer = setTimeout(() => { restartArmed.value = false; }, 4000);
        return;
      }
      restartArmed.value = false;
      trackEvent('cook-restarted');
      timers.value = [];
      ticks.value = {};
      rung.clear();
      phase.value = 'prep';
      prepIndex.value = 0;
      stepIndex.value = 0;
      try { sessionStorage.removeItem(SESSION_KEY(props.id)); } catch { /* nothing */ }
      if (props.hat) {
        clearTimeout(pushTimer);
        await dbDelete(livePath()).catch(() => {});
        const old = await dbGet(tracePath(props.hat, props.id)).catch(() => null);
        if (old) {
          await dbPut(`${coursePath(props.hat)}/lessons/${props.id}/earlier/${Date.now()}`, old).catch(() => {});
          await dbDelete(tracePath(props.hat, props.id)).catch(() => {});
        }
        await markLesson(props.hat, props.id, { startedAt: Date.now() }).catch(() => {});
        logTrace(props.hat, props.id, { type: 'start' });
      }
      scrollPageToTop();
    }

    function quit () {
      logTrace(props.hat, props.id, { type: 'quit', phase: phase.value });
      try { sessionStorage.removeItem(SESSION_KEY(props.id)); } catch { /* nothing */ }
      router.push(`/lesson/${props.id}`);
    }

    onMounted(() => {
      restore();
      if (!sessionStorage.getItem(SESSION_KEY(props.id))) logTrace(props.hat, props.id, { type: 'start' });
      if (props.hat) unsubscribe = subscribe(livePath(), applyLive);
      keepAwake();
      document.addEventListener('visibilitychange', onVisible);
      watchTick = setInterval(watchTimers, 1000);
    });
    onBeforeUnmount(() => {
      unsubscribe?.();
      clearTimeout(pushTimer);
      clearInterval(watchTick);
      document.removeEventListener('visibilitychange', onVisible);
      wakeLock?.release?.().catch?.(() => {});
    });

    // --- the probe (utils/probe.js) --------------------------------------------
    // Read every 15 s while cook mode is open; the two moments worth saying
    // (oven at roasting heat, meat at its target) chime and land in the
    // teacher's chat, where the cook already looks.
    const { status: probeStatus } = useProbe({
      target: () => lesson.value?.probe?.doneAt || null,
      onMoment: (moment) => {
        chime();
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        logTrace(props.hat, props.id, { type: 'probe', kind: moment.kind, meat: store.state.probe?.meat ?? null, ambient: store.state.probe?.ambient ?? null });
        announce(props.hat, moment.text, { screen: 'cook', lessonId: props.id, label: 'the probe' }).catch(() => {});
      }
    });
    // Shown through prep AND cooking (probeChip): the probe is often already
    // in the preheating oven during prep. Hidden on the "On the plate" screen.
    const probeChipShown = computed(() => probeChip(phase.value, probeStatus.value, store.state.probe));
    const probeShown = computed(() => probeChipShown.value.probe);
    const probeChipStatus = computed(() => probeChipShown.value.status);

    // Home's "Start over" arrives as ?fresh=1 (two taps already given there).
    const route = useRoute();
    onMounted(() => { if (route.query.fresh) { restartArmed.value = true; startOver(); router.replace({ query: {} }); } });

    return { restartArmed, startOver, probeShown, probeStatus, probeChipStatus, lesson, showTips, phase, prepIndex, prepStep, goPrep, startSteps, step, stepIndex, go, visibleTimers, timerForStep, startTimer, extendTimer, dismissTimer, isTicked, tick, upNext, finish, quit, formatSeconds, primeAudio, sentences, headline };
  }
};
</script>

<style scoped>
.cook { padding-bottom: 6rem; }
.cook__step { min-height: 70vh; }
.cook__label { display: block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); margin: 0 0 0.2rem; }
.cook__lines { margin: 0; padding-left: 1.4rem; font-size: 1.02rem; line-height: 1.4; }
.cook__lines li + li { margin-top: 0.45rem; }
.cook__lines li::marker { color: var(--muted); font-weight: 700; }
.cook__note { font-size: 0.98rem; line-height: 1.4; border-radius: var(--radius); padding: 0.55rem 0.85rem; margin: 0; }
.cook__tip { background: var(--accent-soft); }
.cook__do { gap: 0.55rem; }
.check { display: flex; gap: 0.8rem; align-items: flex-start; cursor: pointer; font-size: 1.02rem; line-height: 1.4; }
.check--done { opacity: 0.5; text-decoration: line-through; }
.check__box { width: 1.5rem; height: 1.5rem; margin: 0.1rem 0 0; accent-color: var(--green); flex: 0 0 auto; }
.cook__check { background: var(--green-soft); }
.cook__nav { display: grid; grid-template-columns: 1fr 2fr; gap: 0.75rem; margin-top: 0.5rem; }
.cook__dots { list-style: none; display: flex; gap: 0.35rem; justify-content: center; padding: 0; margin: 0.25rem 0 0; }
.cook__dots li { width: 0.6rem; height: 0.6rem; border-radius: 50%; background: var(--line); cursor: pointer; }
.cook__dots li.past { background: var(--green); }
.cook__dots li.on { background: var(--accent); transform: scale(1.3); }
.cook__exits { display: flex; justify-content: center; gap: 1rem; flex-wrap: wrap; }
.cook__quit--sure { color: var(--accent); font-weight: 700; }
.cook__quit { background: none; border: 0; color: var(--muted); font-size: 0.85rem; padding: 0.5rem; }
</style>
